import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Text,
  TextInput,
  Button,
  Accordion,
  ActionIcon,
  Flex,
  Card,
  Tabs,
  Select,
} from "@mantine/core";
import { useServicePlanStyles } from "../style";
import { useSelector } from "react-redux";
import { decrypt } from "../../../utils";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { FiCalendar, FiEdit } from "react-icons/fi";
import { PiWarningBold } from "react-icons/pi";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IoIosCloseCircle } from "react-icons/io";
import { CiImageOn } from "react-icons/ci";
import { TbKeyboard } from "react-icons/tb";
import SignaturePad from "signature_pad";
import { useMediaQuery } from "@mantine/hooks";
import { uploadFileToS3Api } from "../../../utils/PresignedUrlFunction";
import moment from "moment";
import { requestCarePlanSignature } from "../../../services/supportplan";
import { MdKeyboardArrowLeft } from "react-icons/md";
import PenSvg from "../../Svgs/PenSvg";
import RedMailSvg from "../../Svgs/RedMailSvg";
import { ServicePlanPDFPreview } from "../../ServicePlanPreview";
import WhiteCheckMarkSvg from "../../Svgs/WhiteCheckMarkSvg";
import { IoClose } from "react-icons/io5";

/**
 * Component to upload files to a selected folder or create a new one.
 *
 * @param {boolean} props.isPendingAction - whether the action is pending
 * @param {Object} props.uploadedFiles - uploaded files object
 */

const SignatureStep = ({
  data,
  onChange,
  view,
  edit,
  getPlanPayload,
  getPlanPayloadForPdf,
  activeStep,
  uploadedFiles,
  taskTypeDetails,
}) => {  
  const { classes, cx } = useServicePlanStyles();
  const { profile } = useSelector((state) => state?.profile);
  const [requestSignature, setRequestSignature] = useState([]);
  const [isRequestSign, setIsRequestSign] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [signDocument, setSignDocument] = useState(null);
  const [currentTab, setCurrentTab] = useState("draw");
  const signatureCanvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [signatureTextValue, setSignatureTextValue] = useState("");
  const [isCreateSignature, setIsCreateSignature] = useState(false);
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const [signatureError, setSignatureError] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const { allStaffWithSignature } = useSelector((state) => state?.staff);
  const [staffForSign, setStaffForSign] = useState(null);
  const signRecord = staffForSign?.signature || null;

  const handleTabChange = (value) => {
    if (currentTab === "draw") {
      setSignatureTextValue("");
      setSelectedImage(null);
      setCurrentTab(value);
    } else if (currentTab === "type") {
      signaturePadRef.current?.clear();
      setSelectedImage(null);
      setCurrentTab(value);
    } else {
      signaturePadRef.current?.clear();
      setSignatureTextValue("");
      setCurrentTab(value);
    }
  };

  useEffect(() => {
    const checkCanvas = () => {
      const canvas = signatureCanvasRef.current;

      if (canvas) {
        signaturePadRef.current?.off(); // remove old listeners
        canvas.width = isMobileScreen ? 300 : 620;
        canvas.height = 160;
        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: "rgba(255,255,255,0)",
          penColor: "rgb(0,0,0)",
        });
      } else {
        setTimeout(checkCanvas, 50);
      }
    };

    checkCanvas();
  }, [isCreateSignature]);

  useEffect(() => {
    if (edit) {
      setIsApproved(true);
    }
  }, [edit]);

  const handleSignFieldChange = (field, value) => {
    setSignDocument({
      ...signDocument,
      [field]: value,
    });
  };

  const handleUseSignature = () => {
    let imageObj = {};

    if (signRecord.signature) {
      imageObj.signature =
        decrypt(signRecord.signature) || signRecord.signature;
    } else if (signRecord.signatureImage) {
      imageObj.signatureImage =
        decrypt(signRecord.signatureImage) || signRecord.signatureImage;
    } else if (signRecord.signatureText) {
      imageObj.signatureText = signRecord.signatureText;
    }
    // Remove old signature-related properties first
    const { signature, signatureImage, signatureText, ...rest } =
      signDocument || {};

    const updatedSignObj = {
      ...rest,
      signatureRequestStatus: "success",
      timestamp: new Date().toISOString(),
      email: staffForSign?.email || null,
      ...imageObj,
    };

    onChange(
      data.map((item) =>
        item.id === signDocument?.id ? updatedSignObj : item,
      ),
    );

    setSignDocument(null);
  };

  const handleAddSignature = async () => {
    try {
      setBtnLoading(true);
      const hasDrawing =
        signaturePadRef.current && !signaturePadRef.current.isEmpty();
      const hasImage = !!selectedImage;
      const hasSignatureText = !!signatureTextValue;

      let validationError = "";

      if (!hasDrawing && !hasImage && !hasSignatureText) {
        validationError = "Please provide a signature.";
      }

      if (validationError) {
        setSignatureError(validationError);
        return;
      }
      let imageObj = {};

      if (hasDrawing) {
        const dataUrl = signaturePadRef.current.toDataURL("image/png");
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const fileToUpload = new File(
          [blob],
          `careplan_signature_${Date.now()}.png`,
          {
            type: "image/png",
          },
        );
        const result = await uploadFileToS3Api({ file: fileToUpload });
        imageObj.signature = result?.uploadURL;
      } else if (hasImage) {
        const result = await uploadFileToS3Api({ file: selectedImage });
        imageObj.signatureImage = result?.uploadURL;
      } else if (hasSignatureText) {
        imageObj.signatureText = signatureTextValue;
      }

     onChange(
        data.map((item) =>
          item.id === signDocument?.id
            ? {
                ...signDocument,
                ...imageObj,
                signatureRequestStatus: "success",
                timestamp: new Date().toISOString(),
                email: staffForSign?.email || null,
              }
            : item,
        ),
      );

      setIsCreateSignature(false);
      setSignDocument(null);
      signaturePadRef.current?.clear();
      setSignatureTextValue("");
      setSelectedImage(null);
      setStaffForSign(null);
    } catch (error) {
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleRequestSignature = async () => {
    try {
      setBtnLoading(true);
      let payload = {
        planData: getPlanPayload(),
        requestSignature,
        fileUrl: uploadedFiles?.fileUrl,
        businessName: profile?.business?.companyName,
      };
      console.log("🚀 ~ handleRequestSignature ~ payload:", payload);
      await requestCarePlanSignature(payload);
      setIsRequestSign(false);
      setRequestSignature([]);
    } catch (error) {
    } finally {
      setBtnLoading(false);
    }
  };

  const mode = isCreateSignature
    ? "createSignature"
    : signDocument
      ? "signDocument"
      : isRequestSign
        ? "requestSignature"
        : "list";

  const handleBack = () => {
    if (mode === "createSignature") {
      setIsCreateSignature(false);
    }

    if (mode === "signDocument") {
      setSignDocument(null);
    }

    if (mode === "requestSignature") {
      setIsRequestSign(false);
      setRequestSignature([]);
      setEmailValue("");
    }
  };

  const handleAddEmail = () => {
    const email = emailValue.trim();
    if (email) {
      setRequestSignature([...requestSignature, { email }]);
      setEmailValue("");
    }
  };

  const handleRemoveEmail = (email) => {
    const updated = requestSignature.filter(
      (reqSign) => reqSign?.email !== email,
    );
    setRequestSignature(updated);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const facilitySignObj = data?.find(({ position }) => position === "Facility");

  const handleSignDocumentClick = () => {
    if (facilitySignObj?.userId) {
      const selectedStaff = allStaffWithSignature?.find(
        (s) => s?._id === facilitySignObj?.userId,
      );
      setSignDocument(facilitySignObj);
      setStaffForSign(selectedStaff);
    } else {
      const user = allStaffWithSignature?.find(
        (s) => s?._id === profile?.user?.id,
      );
      setSignDocument({
        ...facilitySignObj,
        fullName: user?.name || "",
        userId: user?._id || "",
      });
      setStaffForSign(user);
    }
  };

  return (
    <Box className={classes.formSection}>
      <Accordion
        className={classes.accordionStyle}
        chevron={<IconChevronDown />}
        chevronPosition="right"
        chevronSize={20}
        variant="separated"
        multiple
        defaultValue={["review", "signatures"]}
      >
        {!signDocument && !isCreateSignature && !isRequestSign && (
          <Accordion.Item value="review">
            <Accordion.Control>Review & Sign</Accordion.Control>
            <Accordion.Panel>
              <ServicePlanPDFPreview
                planPayload={getPlanPayloadForPdf()}
                taskTypeDetails={taskTypeDetails}
                businessName={profile?.business?.companyName}
              />
              <Flex align={"center"} justify={"center"} gap={5} mt={20}>
                {!isApproved && !view && (
                  <>
                    <Button
                      onClick={() => {
                        setIsApproved(true);
                        handleSignDocumentClick();
                      }}
                      className={cx(classes.btnStyle, classes.greenBtnStyle)}
                    >
                      <WhiteCheckMarkSvg />
                      Approve & Sign
                    </Button>
                  </>
                )}
              </Flex>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {isApproved && !view && (
          <Accordion.Item value="signatures">
            <Accordion.Control>Signatures</Accordion.Control>
            <Accordion.Panel>
              <Flex align="center" justify="center" gap={5}>
                {mode === "list" && (
                  <>
                    <Button
                      onClick={() => {
                        setIsRequestSign(false);
                        handleSignDocumentClick();
                      }}
                      className={cx(classes.btnStyle, classes.greenSignBtn)}
                    >
                      <PenSvg />
                      Sign Document
                    </Button>
                  </>
                )}
                {mode === "list" && (
                  <>
                    <Button
                      className={cx(
                        classes.btnStyle,
                        classes.requestSignatureBtn,
                      )}
                      onClick={() => {
                        setIsCreateSignature(false);
                        setIsRequestSign(true);
                      }}
                    >
                      <RedMailSvg />
                      Request Signature
                    </Button>
                  </>
                )}
              </Flex>
              {mode !== "list" && (
                <Flex
                  align="center"
                  gap={6}
                  mx={10}
                  my={20}
                  onClick={handleBack}
                  style={{
                    cursor: "pointer",
                    width: "fit-content",
                  }}
                >
                  <MdKeyboardArrowLeft size={28} />
                  <Text fw={400} fz={14} c="#262626">
                    Back
                  </Text>
                </Flex>
              )}
              {isRequestSign && (
                <Box>
                  <Card className={classes.signatureRequestCard}>
                    <span className="warningIcon">
                      <PiWarningBold />
                    </span>
                    <h6 className="cardHdng">Request Signature</h6>
                    <p className="cardDiscription">
                      Please verify that you are sending the right individual
                      this service plan.
                    </p>
                    <Flex className="imgAndFieldParent" align="center" gap={8}>
                      <img src="/assets/images/pdf-svg.png" />
                      <TextInput
                        label="Email Address"
                        placeholder="Enter email address"
                        value={emailValue}
                        onChange={(e) => {
                          setEmailValue(e.target.value);
                        }}
                        onKeyDown={handleEmailKeyDown}
                        onBlur={handleAddEmail}
                        className={cx(classes.inputStyle, classes.inputFlex)}
                        rightSection={
                          <ActionIcon
                            size="sm"
                            variant="transparent"
                            onClick={handleAddEmail}
                          >
                            <IconPlus size={18} />
                          </ActionIcon>
                        }
                      />
                    </Flex>
                    {requestSignature?.length > 0 && (
                      <Flex className={classes.requestedSignaturesEmails}>
                        {requestSignature?.map(({ email }, i) => (
                          <Flex key={i} className={classes.emailAndBtnTag}>
                            <p>{email}</p>
                            <ActionIcon
                              size="xs"
                              variant="transparent"
                              onClick={() => handleRemoveEmail(email)}
                            >
                              <IoClose />
                            </ActionIcon>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                    <Button
                      className={cx(
                        classes.btnStyle,
                        classes.requestSignatureBtn,
                        classes.sendReqSignBtn,
                      )}
                      onClick={handleRequestSignature}
                      loading={btnLoading}
                      disabled={requestSignature.length === 0}
                    >
                      <RedMailSvg />
                      Request Signature
                    </Button>
                    {/* </Flex> */}
                  </Card>
                </Box>
              )}
              {signDocument && !isRequestSign && (
                <Box w="100%">
                  <Flex className={classes.signatureInfoRow}>
                    <Select
                      label="Staff"
                      placeholder="Select staff"
                      searchable
                      className={cx(classes.inputStyle, classes.inputFlex)}
                      value={staffForSign?._id || null}
                      onChange={(v) => {
                        const selectedStaff = allStaffWithSignature?.find(
                          (s) => s?._id === v,
                        );
                        setStaffForSign(selectedStaff);
                        setSignDocument({
                          ...signDocument,
                          fullName: selectedStaff?.name || "",
                          userId: selectedStaff?._id || "",
                        });
                      }}
                      rightSection={
                        <IconChevronDown size={20} color="#667085" />
                      }
                      data={
                        allStaffWithSignature?.map((s) => ({
                          label: s?.name,
                          value: s?._id,
                        })) || []
                      }
                    />

                    <TextInput
                      label="Title"
                      placeholder="Add Title"
                      value={signDocument?.title}
                      onChange={(e) => {
                        handleSignFieldChange("title", e.target.value);
                      }}
                      className={cx(classes.inputStyle, classes.inputFlex)}
                    />

                    <DateInput
                      className={cx(
                        classes.inputStyle,
                        classes.datePickerStyle,
                      )}
                      icon={<FiCalendar />}
                      label="Date"
                      valueFormat="MMM D, YYYY"
                      w={"25%"}
                      placeholder="mm/dd/yyyy"
                      value={
                        signDocument?.date
                          ? moment(signDocument?.date).toDate()
                          : null
                      }
                      onChange={(value) => {
                        handleSignFieldChange("date", value);
                      }}
                    />
                  </Flex>
                  {isCreateSignature ? (
                    <Box className={classes.createSignatureContent}>
                      <Tabs
                        className={classes.tabs}
                        defaultValue={"draw"}
                        value={currentTab}
                        onTabChange={handleTabChange}
                      >
                        <Tabs.List
                          justify="center"
                          className={classes.tab_list}
                        >
                          <Tabs.Tab className={classes.tab} value="draw">
                            <FiEdit size={20} /> Draw
                          </Tabs.Tab>
                          <Tabs.Tab className={classes.tab} value="type">
                            <TbKeyboard size={25} /> Type
                          </Tabs.Tab>
                          <Tabs.Tab className={classes.tab} value="image">
                            <CiImageOn size={25} /> Image
                          </Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value="draw" className={classes.tabPanel}>
                          <div className={classes.signaturePadParent}>
                            <canvas ref={signatureCanvasRef} />
                          </div>
                        </Tabs.Panel>
                        <Tabs.Panel value="type" className={classes.tabPanel}>
                          <Flex
                            align="center"
                            justify="center"
                            className={classes.textPad}
                          >
                            <TextInput
                              variant="unstyled"
                              value={signatureTextValue}
                              onChange={(event) =>
                                setSignatureTextValue(event.currentTarget.value)
                              }
                            />
                          </Flex>
                        </Tabs.Panel>
                        <Tabs.Panel value="image" className={classes.tabPanel}>
                          <Box>
                            {selectedImage ? (
                              <Flex
                                align="center"
                                justify="center"
                                className={classes.previewBox}
                              >
                                <Box pos="relative" display="inline-block">
                                  <img
                                    src={URL.createObjectURL(selectedImage)}
                                    alt="preview"
                                    className={classes.previewImg}
                                  />

                                  <ActionIcon
                                    variant="transparent"
                                    className={classes.selectedImgClose}
                                    onClick={() => setSelectedImage(null)}
                                  >
                                    <IoIosCloseCircle size={24} color="#FFF" />
                                  </ActionIcon>
                                </Box>
                              </Flex>
                            ) : (
                              <Dropzone
                                accept={IMAGE_MIME_TYPE}
                                onDrop={(files) => setSelectedImage(files[0])}
                                maxSize={2 * 1024 ** 2}
                                multiple={false}
                                classNames={{
                                  root: classes.uploadBox,
                                  inner: "uploadBoxInner",
                                }}
                              >
                                <Flex
                                  direction="column"
                                  align="center"
                                  justify="center"
                                  h="100%"
                                  w="100%"
                                >
                                  <Text
                                    fz={16}
                                    fw={600}
                                    color="#171717"
                                    ff="Inter"
                                  >
                                    <Text
                                      span
                                      td="underline"
                                      fz={16}
                                      fw={600}
                                      color="#171717"
                                      ff="Inter"
                                    >
                                      Click to Upload
                                    </Text>{" "}
                                    or Drag and Drop
                                  </Text>
                                  <Text
                                    fz={12}
                                    fw={500}
                                    c="#979797"
                                    ff="Inter"
                                    mt={8}
                                  >
                                    Maximum File Size 2MB
                                  </Text>
                                </Flex>
                              </Dropzone>
                            )}
                          </Box>
                        </Tabs.Panel>
                        {signatureError ? (
                          <Text fs={14} fw={500} color="red">
                            {signatureError}
                          </Text>
                        ) : null}
                      </Tabs>
                      <Flex gap={12}>
                        <Button
                          className={classes.btnStyle}
                          onClick={handleAddSignature}
                          loading={btnLoading}
                        >
                          Add Signature
                        </Button>
                      </Flex>
                    </Box>
                  ) : (
                    <Box className={classes.addSignatureContent}>
                      {signRecord ? (
                        <Flex className={classes.signatureAndInitialsWrapper}>
                          <Box>
                            <p className="userNameHdng">{staffForSign?.name}</p>
                            <Flex className={classes.signatureAndInitialsRow}>
                              <Box>
                                <p className={classes.signAndInitialLabel}>
                                  Signature
                                </p>
                                <Box
                                  className={classes.signatureAndInitialsBox}
                                >
                                  {signRecord?.signature ? (
                                    <img
                                      src={
                                        decrypt(signRecord?.signature) ||
                                        signRecord?.signature
                                      }
                                      alt="Signature"
                                      className={classes.signatureDrawImg}
                                    />
                                  ) : signRecord?.signatureImage ? (
                                    <img
                                      src={
                                        decrypt(signRecord?.signatureImage) ||
                                        signRecord?.signatureImage
                                      }
                                      className={classes.signatureImg}
                                      alt="Signature"
                                    />
                                  ) : signRecord?.signatureText ? (
                                    <p className={classes.textSignatureStyle}>
                                      {signRecord?.signatureText || "N/A"}
                                    </p>
                                  ) : (
                                    <span>N/A</span>
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <p className={classes.signAndInitialLabel}>
                                  Initials
                                </p>
                                <Box
                                  className={classes.signatureAndInitialsBox}
                                >
                                  {signRecord?.signatureInitials ? (
                                    <img
                                      src={
                                        decrypt(
                                          signRecord?.signatureInitials,
                                        ) || signRecord?.signatureInitials
                                      }
                                      alt="Initials"
                                      className={classes.signatureImg}
                                    />
                                  ) : signRecord?.signatureInitialsText ? (
                                    <p className={classes.textSignatureStyle}>
                                      {signRecord?.signatureInitialsText}
                                    </p>
                                  ) : (
                                    <span>N/A</span>
                                  )}
                                </Box>
                              </Box>
                            </Flex>
                          </Box>
                          <Button
                            className={cx(
                              classes.btnStyle,
                              classes.greenSignBtn,
                            )}
                            onClick={handleUseSignature}
                          >
                            <PenSvg />
                            Sign Document
                          </Button>
                        </Flex>
                      ) : (
                        <Text color="dimmed" ff="Inter">
                          No signature found
                        </Text>
                      )}
                      <Flex className={classes.btnsRow}>
                        <Button
                          className={cx(classes.btnStyle, classes.inputFlex)}
                          onClick={() => setIsCreateSignature(true)}
                        >
                          Change Signature
                        </Button>
                      </Flex>
                    </Box>
                  )}
                </Box>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>

      <Text />
    </Box>
  );
};

export default SignatureStep;
